import {
  Mesh,
  IcosahedronGeometry,
  CylinderGeometry,
  PlaneGeometry,
  TorusGeometry,
  Vector3
} from 'three';
import { Ability } from './Ability.js';
import { settings } from '../config/settings.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import { frame } from '../core/FrameUniforms.js';
import { LAYER } from '../core/Layers.js';
import { saturate, Easing } from '../utils/math.js';
import { getColor } from '../utils/color.js';
import {
  ConstructStyle,
  ConstructGlyph,
  createConstructMaterial,
  createConstructGlyphMaterial,
  syncConstructMaterial
} from '../materials/ConstructMaterial.js';
import { poseCylinder, pointOnRing } from './ConstructUtils.js';

const _a = new Vector3();
const _b = new Vector3();
const _dir = new Vector3();
const _emit = {};

/**
 * PRISM WAKE — a delayed triangular enclosure.
 *
 * The projectile is only the delivery. On arrival it throws three anchors out,
 * draws the sides one after another, then repeatedly refracts energy from the
 * perimeter into the middle. Its combat read is therefore "leave the triangle
 * before it closes", not "dodge another line projectile".
 */
export class PrismWakeAbility extends Ability {
  constructor(context) {
    super('prism', context);
  }

  createShaders() {
    this.prismGeometry = new IcosahedronGeometry(1, 1);
    this.prismMaterial = createConstructMaterial(ConstructStyle.PRISM);
    this.prism = new Mesh(this.prismGeometry, this.prismMaterial);
    this.prism.layers.set(LAYER.VFX);
    this.prism.renderOrder = 13;
    this.group.add(this.prism);

    this.edgeGeometry = new CylinderGeometry(1, 1, 1, 8, 1, true);
    this.edges = [];
    for (let i = 0; i < 3; i++) {
      const material = createConstructMaterial(ConstructStyle.ENERGY);
      const mesh = new Mesh(this.edgeGeometry, material);
      mesh.layers.set(LAYER.VFX);
      mesh.renderOrder = 12;
      mesh.matrixAutoUpdate = false;
      mesh.visible = false;
      this.group.add(mesh);
      this.edges.push({ mesh, material });
    }

    this.nodeGeometry = new TorusGeometry(1, 0.055, 8, 32);
    this.nodes = [];
    for (let i = 0; i < 3; i++) {
      const material = createConstructMaterial(ConstructStyle.PRISM);
      const mesh = new Mesh(this.nodeGeometry, material);
      mesh.layers.set(LAYER.VFX);
      mesh.renderOrder = 13;
      mesh.rotation.x = Math.PI / 2;
      mesh.visible = false;
      this.group.add(mesh);
      this.nodes.push({ mesh, material });
    }

    this.fieldGeometry = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.fieldMaterial = createConstructGlyphMaterial(ConstructGlyph.PRISM);
    this.field = new Mesh(this.fieldGeometry, this.fieldMaterial);
    this.field.layers.set(LAYER.VFX);
    this.field.renderOrder = 8;
    this.field.visible = false;
    this.group.add(this.field);

    this.centre = new Vector3();
    this.vertices = [new Vector3(), new Vector3(), new Vector3()];
    this._seed = 0;
    this._edgeTriggered = [false, false, false];
  }

  createParticles() {
    this.sparks = this.ctx.particles.get('prism.sparks', {
      capacity: 2600,
      shape: ParticleShape.STREAK,
      additive: true,
      stretch: true,
      softFade: 0.35
    });
    this.sparks.uniforms.uDrag.value = 1.4;
    this.sparks.uniforms.uEndSize.value = 0.12;
    this.sparks.uniforms.uFadeOut.value = 0.42;

    this.motes = this.ctx.particles.get('prism.motes', {
      capacity: 2200,
      shape: ParticleShape.SOFT,
      additive: true,
      curl: true,
      softFade: 0.5
    });
    this.motes.uniforms.uDrag.value = 1.7;
    this.motes.uniforms.uEndSize.value = 0.16;
    this.motes.uniforms.uFadeOut.value = 0.38;
    this.moteEmitter = new RateEmitter();
  }

  get impactDuration() {
    return Math.max(0.2, settings.prism.lifetime * settings.global.lifetime);
  }

  get fadeDuration() {
    return Math.max(0.1, settings.prism.fadeTime);
  }

  get instanceCount() {
    return 8;
  }

  onSpawn() {
    this._seed = Math.random() * 10;
    this._edgeTriggered.fill(false);
    this.moteEmitter.reset();
    this.prism.visible = true;
    this.field.visible = false;
    for (const edge of this.edges) edge.mesh.visible = false;
    for (const node of this.nodes) node.mesh.visible = false;
    this._syncTravel();
  }

  _syncTravel() {
    const c = settings.prism;
    const scale = c.prismSize * (0.72 + Easing.outBack(saturate(this.age / 0.18)) * 0.28);
    this.prism.position.copy(this.position).setY(c.travelHeight);
    this.prism.scale.set(scale * 0.72, scale, scale * 0.72);
    this.prism.rotation.set(this.age * c.spin * 0.7, this.age * c.spin, this.age * c.spin * 0.4);
    syncConstructMaterial(this.prismMaterial, c, {
      seed: this._seed,
      reveal: 1,
      fade: 1,
      pulse: 0.3 + 0.2 * Math.sin(this.age * 9)
    });
  }

  _emitAt(position, count, speed = 3) {
    const c = settings.prism;
    const g = settings.global;
    _emit.position = position;
    _emit.radius = c.particleRadius;
    _emit.direction = _dir.set(0, 1, 0);
    _emit.speed = speed * g.particleSpeed;
    _emit.speedVariance = 0.65;
    _emit.spread = 1;
    _emit.inherit = null;
    _emit.anchor = null;
    _emit.size = c.particleSize * g.particleSize;
    _emit.sizeVariance = 0.55;
    _emit.life = c.particleLife * g.particleLifetime;
    _emit.lifeVariance = 0.35;
    _emit.spin = 3;
    _emit.tint = null;
    _emit.time = frame.uTime.value;
    this.sparks.emit(Math.round(count * g.particleCount), _emit);
  }

  onTravel(dt) {
    this._syncTravel();
    const c = settings.prism;
    const count = this.moteEmitter.tick(dt, c.trailRate);
    if (count > 0) {
      _emit.position = this.prism.position;
      _emit.radius = c.prismSize * 0.5;
      _emit.direction = _dir.copy(this.direction).multiplyScalar(-1).setY(0.25).normalize();
      _emit.speed = c.trailSpeed;
      _emit.speedVariance = 0.55;
      _emit.spread = 0.65;
      _emit.inherit = null;
      _emit.anchor = null;
      _emit.size = c.trailSize;
      _emit.sizeVariance = 0.5;
      _emit.life = c.trailLife;
      _emit.lifeVariance = 0.35;
      _emit.spin = 0;
      _emit.tint = null;
      _emit.time = frame.uTime.value;
      this.motes.emit(Math.round(count * settings.global.particleCount), _emit);
    }
  }

  onImpact() {
    const c = settings.prism;
    this.pointAt(1, this.centre).setY(0);
    this.position.copy(this.centre).setY(c.nodeHeight);
    this.field.visible = true;
    this.lightBoost = c.impactLight;

    this.ctx.bursts.spawn(BurstMode.AIR, _a.copy(this.centre).setY(c.nodeHeight), {
      radius: 0.25,
      endRadius: c.radius * 0.7,
      life: 0.55,
      intensity: c.burstIntensity,
      colorA: getColor(c.colorC),
      colorB: getColor(c.colorA),
      colorC: getColor(c.colorB)
    });
    this.ctx.decals.spawn(DecalType.SHOCKWAVE, this.centre, {
      radius: c.radius * 1.15,
      life: 0.7,
      intensity: c.burstIntensity,
      colorA: getColor(c.colorA),
      colorB: getColor(c.colorC)
    });
    this.ctx.shake.add(c.impactShake * settings.global.cameraShake, c.shakeDuration);
    this.ctx.flash.trigger(getColor(c.colorC), c.impactFlash);
  }

  onFade(dt, t) {
    const c = settings.prism;
    const hold = t <= 1 ? t : 1;
    const fade = t <= 1 ? 1 : 1 - (t - 1);
    const radius = c.radius;
    const entry = Math.atan2(-this.direction.z, -this.direction.x);

    for (let i = 0; i < 3; i++) {
      pointOnRing(this.vertices[i], this.centre, radius, entry + i * Math.PI * 2 / 3, c.nodeHeight);
    }

    const fieldSize = radius * 2.35;
    this.field.position.copy(this.centre).setY(c.fieldHeight);
    this.field.scale.set(fieldSize, fieldSize, fieldSize);
    syncConstructMaterial(this.fieldMaterial, c, {
      seed: this._seed,
      reveal: Easing.outCubic(saturate(hold / Math.max(0.01, c.closeTime))),
      fade,
      pulse: 0.5 + 0.5 * Math.sin(this.impactTime * c.pulseSpeed)
    });

    for (let i = 0; i < 3; i++) {
      const local = saturate((this.impactTime - i * c.edgeStagger) / Math.max(0.01, c.edgeTime));
      const reveal = Easing.outCubic(local);
      const edge = this.edges[i];
      edge.mesh.visible = reveal > 0.001;
      poseCylinder(edge.mesh, this.vertices[i], this.vertices[(i + 1) % 3], c.edgeWidth * Math.max(0.04, reveal));
      syncConstructMaterial(edge.material, c, {
        seed: this._seed + i,
        reveal,
        fade,
        pulse: Math.sin(this.impactTime * c.pulseSpeed - i * 1.7) * 0.5 + 0.5
      });

      const node = this.nodes[i];
      node.mesh.visible = reveal > 0.001;
      node.mesh.position.copy(this.vertices[i]);
      node.mesh.rotation.x = Math.PI / 2;
      node.mesh.rotation.z = this.impactTime * c.spin * (i % 2 ? -1 : 1);
      node.mesh.scale.setScalar(c.nodeSize * (0.55 + reveal * 0.45));
      syncConstructMaterial(node.material, c, { seed: this._seed + i * 2.1, reveal, fade, pulse: local });

      if (local > 0 && !this._edgeTriggered[i]) {
        this._edgeTriggered[i] = true;
        this._emitAt(this.vertices[i], c.edgeSparks, c.particleSpeed);
      }
    }

    const corePulse = 1 + Math.sin(this.impactTime * c.pulseSpeed) * c.corePulse;
    this.prism.position.copy(this.centre).setY(c.coreHeight);
    this.prism.scale.setScalar(c.prismSize * c.coreScale * corePulse * fade);
    this.prism.rotation.set(this.impactTime * c.spin * 0.7, this.impactTime * c.spin, 0.35);
    syncConstructMaterial(this.prismMaterial, c, {
      seed: this._seed,
      reveal: 1,
      fade,
      pulse: corePulse
    });

    const moteCount = this.moteEmitter.tick(dt, c.holdRate);
    if (moteCount > 0) {
      const vertex = this.vertices[Math.floor(Math.random() * 3)];
      _emit.position = vertex;
      _emit.radius = c.nodeSize;
      _emit.direction = _dir.copy(this.centre).sub(vertex).normalize();
      _emit.speed = c.particleSpeed;
      _emit.speedVariance = 0.35;
      _emit.spread = 0.18;
      _emit.inherit = null;
      _emit.anchor = this.centre;
      _emit.size = c.particleSize;
      _emit.sizeVariance = 0.5;
      _emit.life = c.particleLife;
      _emit.lifeVariance = 0.3;
      _emit.spin = 0;
      _emit.tint = null;
      _emit.time = frame.uTime.value;
      this.motes.emit(Math.round(moteCount * settings.global.particleCount), _emit);
    }
  }

  onDestroy() {
    this.prism.visible = false;
    this.field.visible = false;
    for (const edge of this.edges) edge.mesh.visible = false;
    for (const node of this.nodes) node.mesh.visible = false;
  }

  dispose() {
    this.prismGeometry.dispose();
    this.edgeGeometry.dispose();
    this.nodeGeometry.dispose();
    this.fieldGeometry.dispose();
    this.prismMaterial.dispose();
    this.fieldMaterial.dispose();
    for (const edge of this.edges) edge.material.dispose();
    for (const node of this.nodes) node.material.dispose();
    super.dispose();
  }
}
